import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AiOutlineCheckCircle, AiOutlineMail, AiOutlineReload } from 'react-icons/ai';
import { resendVerificationEmail } from '../api/users';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Input from '../components/common/Input';

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [showResendForm, setShowResendForm] = useState(false);

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsResending(true);
    setResendMessage('');

    try {
      await resendVerificationEmail(email.trim());
      setResendMessage('Verification email sent successfully! Please check your inbox.');
      setShowResendForm(false);
    } catch (err) {
      setResendMessage(err instanceof Error ? err.message : 'An error occurred while resending the verification email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 md:pt-20">
      <section className="px-4 py-10 md:py-14">
        <div className="mx-auto max-w-5xl rounded-[2rem] border border-[#8f7e69]/25 bg-[#fbf7f0]/90 px-6 py-10 shadow-[0_24px_70px_rgba(44,32,27,0.12)] sm:px-10 md:px-14">
          <p className="text-xs uppercase tracking-[0.3em] text-[#8a775f]">Account</p>
          <div className="mt-5 grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] md:items-end">
            <div>
              <h1 className="font-serif text-4xl leading-tight text-[#231f1c] sm:text-5xl">Check your inbox.</h1>
            </div>
            <p className="text-sm leading-7 text-[#5b5048] md:max-w-sm md:justify-self-end">
              One more step and you're in. Verify your email, then come back and sign in.
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 pb-16">
        <div className="mx-auto max-w-xl">
          <Card className="space-y-6">
            <div className="flex items-start gap-4 rounded-[1.5rem] border border-[#b8d6c0] bg-[#eef8f0] p-5">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/80 text-[#3e7a50]">
                <AiOutlineMail className="text-2xl" />
              </div>
              <div>
                <h2 className="font-serif text-2xl text-[#2c201b]">We've sent the link.</h2>
                <p className="mt-2 text-sm leading-7 text-[#5b5048]">
                  Look for a verification email in your inbox or spam folder, then click the link to activate your account.
                </p>
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-[#8f7e69]/20 bg-[#f6efe5] p-5">
              <div className="flex items-start gap-3">
                <AiOutlineCheckCircle className="mt-1 text-xl text-[#5a465f]" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-[#8a775f]">What happens next</p>
                  <ul className="mt-3 space-y-2 text-sm leading-7 text-[#5b5048]">
                    <li>Check your inbox and spam folder.</li>
                    <li>Open the verification email.</li>
                    <li>Use the link inside, then return to sign in.</li>
                  </ul>
                </div>
              </div>
            </div>

            {resendMessage && (
              <div
                className={`rounded-xl border p-3 text-sm ${
                  resendMessage.includes('successfully')
                    ? 'border-green-200 bg-green-50/90 text-green-800'
                    : 'border-red-200 bg-red-50/90 text-red-700'
                }`}
              >
                {resendMessage}
              </div>
            )}

            <div className="space-y-3">
              <Link to="/login" className="block">
                <Button type="button" fullWidth size="large">
                  Go to login
                </Button>
              </Link>
              <Button type="button" variant="secondary" fullWidth onClick={() => navigate('/login')}>
                I've verified my email
              </Button>
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={() => setShowResendForm((prev) => !prev)}
                className="inline-flex items-center justify-center gap-2"
              >
                <AiOutlineReload className="text-base" />
                Resend verification email
              </Button>
            </div>

            {showResendForm && (
              <div className="rounded-[1.5rem] border border-[#8f7e69]/20 bg-[#f6efe5] p-5">
                <h3 className="font-serif text-2xl text-[#2c201b]">Send another verification email.</h3>
                <form onSubmit={handleResendVerification} className="mt-4 space-y-4">
                  <Input
                    id="resend-email"
                    type="email"
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                  />
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" isLoading={isResending} disabled={!email.trim()}>
                      Send email
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowResendForm(false);
                        setEmail('');
                        setResendMessage('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <p className="text-center text-sm text-[#6a5a4f]">
              Didn't receive it? Give it a minute, then try the resend option above.
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default EmailVerificationPage;
